import styled from '@emotion/styled';
import { colors } from '@innovationdepartment/proxima-ui';

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 24px;
  padding-bottom: 16px;
`;

/* The theme pads small outlined buttons wider and gives them a border, so small contained buttons get the same padding and a clear border to match their size */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  .MuiButton-contained.MuiButton-sizeSmall {
    padding: 6px 14px;
    border: 1px solid transparent;
  }
`;

export const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: 112px;
  padding: 14px 16px;
  border: 1px solid ${colors.neutral30};
  border-radius: 8px;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.neutral100};
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: ${colors.neutral50};
  }

  &:focus {
    outline: none;
    border-color: ${colors.blue60};
    box-shadow: 0 0 0 3px ${colors.blue10};
  }
`;

/* The generator prompt's visual box: the textarea, then a fixed strip holding the character counter, so text never scrolls beneath the counter */
export const GeneratorPromptBox = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.neutral30};
  border-radius: 8px;
  background: ${colors.white};

  &:focus-within {
    border-color: ${colors.blue60};
    box-shadow: 0 0 0 3px ${colors.blue10};
  }
`;

/** The generator's main prompt, sized for a multi-sentence description. */
export const GeneratorPromptTextarea = styled(PromptTextarea)`
  display: block;
  min-height: 190px;
  padding-bottom: 4px;
  border: none;
  border-radius: 8px 8px 0 0;
  background: transparent;

  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
`;

export const PromptFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > button {
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

export const Counter = styled.span<{ near: boolean }>`
  align-self: flex-end;
  padding: 4px 12px 8px;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ near }) => (near ? colors.orange60 : colors.neutral50)};
  font-variant-numeric: tabular-nums;
`;

export const InlineErrors = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: ${colors.red60};
`;

export const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.neutral80};
`;

/* Name and Description on the Define step read larger than the form fields elsewhere */
export const DefinitionFieldLabel = styled(FieldLabel)`
  font-size: 16px;
`;

export const TextInput = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${colors.neutral30};
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.neutral100};
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${colors.blue60};
    box-shadow: 0 0 0 3px ${colors.blue10};
  }
`;

/* The preview's reading of the prompt, and what it could not express, under a heading a step below the audience name */
export const InterpretationSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
`;

export const InterpretationHeading = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral100};
`;

export const InterpretationText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.neutral100};
`;

export const InterpretationUnmappedLabel = styled.span`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.orange70};
`;

export const InterpretationList = styled.ul`
  margin: 0;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.neutral100};
`;

export const Notice = styled.div<{ tone: 'warning' | 'error' | 'info' }>`
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid
    ${({ tone }) => {
      if (tone === 'warning') return colors.yellow30;
      if (tone === 'error') return colors.red20;
      return colors.blue20;
    }};
  background: ${({ tone }) => {
    if (tone === 'warning') return colors.yellow5;
    if (tone === 'error') return colors.red5;
    return colors.blue5;
  }};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ tone }) => {
    if (tone === 'warning') return colors.yellow90;
    if (tone === 'error') return colors.red80;
    return colors.blue80;
  }};

  ul,
  ol {
    margin: 6px 0 0;
    padding-left: 20px;
  }
`;

export const Questions = styled.ol`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: ${colors.neutral90};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

export const Thinking = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border: 1px dashed ${colors.neutral30};
  border-radius: 10px;
  color: ${colors.neutral60};
  font-size: 14px;

  /* SpinningLoader has no size prop and draws at 140px, so size it to sit inline with the text */
  & svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }
`;

// Targets

export const PlatformRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

export const PlatformOption = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({ selected }) => (selected ? colors.blue60 : colors.neutral30)};
  background: ${({ selected }) => (selected ? colors.blue5 : colors.white)};
  font-family: inherit;
  font-size: 15px;
  color: ${colors.neutral100};
  cursor: pointer;
  text-align: left;

  &:disabled {
    color: ${colors.neutral40};
    cursor: not-allowed;
  }

  small {
    font-size: 12px;
    color: ${colors.neutral50};
  }
`;

export const RadioDot = styled.span<{ selected: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${({ selected }) => (selected ? colors.blue60 : colors.neutral40)};
  box-shadow: inset 0 0 0 2px ${colors.white};
  background: ${({ selected }) => (selected ? colors.blue60 : colors.white)};
  flex-shrink: 0;
`;

export const AccountGroup = styled.div`
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  overflow: hidden;
`;

export const AccountGroupTitle = styled.div`
  padding: 10px 14px;
  background: ${colors.neutral5};
  border-bottom: 1px solid ${colors.neutral20};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.neutral60};
`;

export const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 14px;
  border-bottom: 1px solid ${colors.neutral10};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const AccountLead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* Greys out an ineligible account; the reason icon beside it stays at full strength */
export const AccountChoice = styled.span<{ dimmed: boolean }>`
  opacity: ${({ dimmed }) => (dimmed ? 0.5 : 1)};
`;

export const IneligibleIcon = styled.span`
  display: inline-flex;
  border-radius: 4px;
  cursor: help;

  &:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: 2px;
  }
`;

export const AccountMeta = styled.span`
  font-size: 12px;
  color: ${colors.neutral60};
`;

export const TosBadge = styled.span<{ accepted: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ accepted }) => (accepted ? colors.green5 : colors.red5)};
  color: ${({ accepted }) => (accepted ? colors.green70 : colors.red70)};
  white-space: nowrap;
`;

/* The terms check outcome and Retry on the right of an unaccepted account's row */
export const TosActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
`;

/* The Meta terms link and its copy button, beside the ToS badge */
export const TosLinkGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding-left: 4px;
`;

/* Retry and copy icon buttons, sized to the 12px row they sit in */
export const TosIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid ${colors.neutral30};
  border-radius: 4px;
  background: transparent;
  color: ${colors.neutral80};
  cursor: pointer;

  svg {
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background: ${colors.neutral5};
    color: ${colors.neutral100};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue60};
    outline-offset: 2px;
  }
`;

export const TosLink = styled.a`
  font-size: 12px;
  color: ${colors.blue60};
`;

export const InlineRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`;

export const CountResult = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CountNumber = styled.span<{ stale: boolean }>`
  font-size: 22px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ stale }) => (stale ? colors.neutral40 : colors.neutral100)};
  text-decoration: ${({ stale }) => (stale ? 'line-through' : 'none')};
`;

export const SeedMixRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
`;

export const SeedMixField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.neutral80};
  }
`;

export const CountNote = styled.span<{ tone?: 'warning' | 'muted' | 'error' }>`
  font-size: 13px;
  color: ${({ tone }) => {
    if (tone === 'warning') return colors.orange70;
    if (tone === 'error') return colors.red60;
    return colors.neutral60;
  }};
`;

// Double-sidebar layout

export const Layout = styled.div<{ leftOpen: boolean; rightOpen: boolean }>`
  display: grid;
  grid-template-columns: ${({ leftOpen }) => (leftOpen ? '360px' : '44px')} minmax(520px, 1fr) ${({
    rightOpen
  }) => (rightOpen ? '260px' : '44px')};
  gap: 20px;
  align-items: start;
  min-width: 0;
`;

export const Sidebar = styled.aside`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 180px);
  border: 1px solid ${colors.neutral20};
  border-radius: 10px;
  background: ${colors.white};
  overflow: hidden;
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid ${colors.neutral20};
  background: ${colors.neutral0};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral100};
`;

export const SidebarBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px 18px;
  overflow-y: auto;
`;

export const CollapseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${colors.neutral20};
  border-radius: 6px;
  background: ${colors.white};
  color: ${colors.neutral70};
  cursor: pointer;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${colors.neutral5};
    color: ${colors.neutral100};
  }
`;

export const CollapsedRail = styled.button`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 44px;
  padding: 12px 0;
  border: 1px solid ${colors.neutral20};
  border-radius: 10px;
  background: ${colors.neutral0};
  color: ${colors.neutral70};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  span {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    white-space: nowrap;
  }

  svg {
    font-size: 18px;
  }

  &:hover {
    color: ${colors.neutral100};
    background: ${colors.neutral5};
  }
`;

export const Center = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const MapperDomain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const MapperDomainTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.neutral60};
  padding-bottom: 2px;
`;

export const MapperField = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  padding: 1px 0;
`;

export const MapperMarks = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding-top: 9px;
  flex-shrink: 0;
`;

export const SidebarCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${colors.blue60};
  color: ${colors.white};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  box-sizing: border-box;
`;

/* Sits under the prompt, split from it by a divider with 15px on each side */
export const GeneratorCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 1px;
  padding-top: 15px;
  border-top: 1px solid ${colors.neutral20};
`;

export const GeneratorTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral100};
`;

export const GeneratorDescription = styled.span`
  font-size: 14px;
  color: ${colors.neutral100};
`;

export const CountPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StripRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  > span:first-of-type {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.neutral80};
    padding-top: 2px;
    white-space: nowrap;
  }

  > div {
    flex: 1;
    min-width: 0;
  }
`;

export const CountRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

/* Holds the Preview Audience blocker Pill and announces it when it appears */
export const CountBlocker = styled.div`
  display: flex;
`;

export const EmptyRules = styled.div`
  padding: 28px 20px;
  border: 1px dashed ${colors.neutral30};
  border-radius: 10px;
  color: ${colors.neutral60};
  font-size: 14px;
  text-align: center;
  line-height: 1.6;
`;

/* Frames a wizard panel, the definition or the Deliver-to form, under its 24px heading */
export const DefinitionPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 24px;
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  background: ${colors.white};
  min-width: 0;
`;

/* The first section under a panel heading, which already carries the divider */
export const DeliverSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PanelSectionHeading = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.neutral100};
`;

/* A section of the definition panel below Name and Description, set off by a divider */
export const PanelSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${colors.neutral20};
`;

export const DefinitionHeading = styled.h2`
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.neutral20};
  font-size: 24px;
  font-weight: 600;
  color: ${colors.neutral100};
`;

export const SidebarTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const CountActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* Holds a Deliver-to card at a readable width, on the Deliver step and on the detail page */
export const DeliverToBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;
